import React, { useMemo } from 'react';
import { useLocation } from 'react-router';

import { FiveHundredErrorPage } from '@sm/webassets';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import { usePricingExperience } from '~shared/hooks/usePricingExperience';
import { logAmplitudeEvent, logPricingPageLoad } from '~pricing/pages/shared/index';
import { AMPLITUDE_PAGE_EVENTS } from '~shared/util';

export function withPageHandler<P>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> {
  return props => {
    const { search } = useLocation();

    const query = new URLSearchParams(search);
    const featureId = query.get('selected_feature');
    const categoryId = query.get('selected_category');

    const { pageName, utSource } = useTrackingDetails();

    useMemo(() => {
      logPricingPageLoad({ pageName, utSource });
    }, [pageName, utSource]);

    useMemo(() => {
      if (categoryId || featureId) {
        logAmplitudeEvent({
          eventName: `selected ${featureId ? 'feature' : 'category'} from upgrade cta`,
          pageName,
          amplitudeEvent: AMPLITUDE_PAGE_EVENTS.experimentEvent,
          otherData: {
            [`selected${categoryId ? 'Category' : 'Feature'}`]: categoryId ?? featureId ?? '',
          },
        });
      }
    }, [categoryId, featureId, pageName]);

    const { packages } = usePricingExperience();

    // no data is no page
    const error = !Array.isArray(packages) || !packages.length;

    return (
      <Choose>
        {/* if there was an issue loading the data from pricingsvc, we've got nothing to show. bail */}
        <When condition={error}>
          <FiveHundredErrorPage />
        </When>
        <When condition={!error && packages.length}>
          <WrappedComponent {...props} />
        </When>
      </Choose>
    );
  };
}

export default withPageHandler;
